/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Loader2, Edit, Trash2, GitBranch, Play, Layout, Rocket, MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";
import { TestFlowDialog } from "@/components/TestFlowDialog";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";

export default function FlowsPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [flowToDelete, setFlowToDelete] = useState<any>(null);
  const [flowToTest, setFlowToTest] = useState<any>(null);

  // Fetch all flows
  const { data: flows, isLoading } = useQuery<any[]>({
    queryKey: ["/api/flow-automation/flows"],
  });

  // Pagination
  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    paginatedItems: paginatedFlows,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(flows || [], 9);

  // Delete flow mutation
  const deleteMutation = useMutation({
    mutationFn: async (flowId: string) => {
      return apiRequest("DELETE", `/api/flow-automation/flows/${flowId}`);
    },
    onSuccess: () => {
      toast({
        title: "Flow deleted",
        description: "The flow has been deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/flows"] });
      setFlowToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting flow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ flowId, isActive }: { flowId: string; isActive: boolean }) => {
      return apiRequest("PATCH", `/api/flow-automation/flows/${flowId}`, { isActive });
    },
    onSuccess: () => {
      toast({
        title: "Flow updated",
        description: "Flow status has been updated",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/flows"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating flow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create campaign from flow mutation
  const createCampaignFromFlowMutation = useMutation({
    mutationFn: async (flow: { id: string; name: string }) => {
      const res = await apiRequest("POST", "/api/campaigns", {
        name: `${flow.name} Campaign`,
        type: "outbound",
        flowId: flow.id,
        status: "pending",
      });
      return res.json();
    },
    onSuccess: (campaign: any) => {
      toast({
        title: "Campaign Created!",
        description: `Campaign "${campaign.name}" created. Add contacts and agent to start.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      setLocation(`/campaigns/${campaign.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" data-testid="text-page-title">Visual Flows</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            Create and manage conversation flows with drag-and-drop visual builder
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setLocation('/app/flows/templates')}
            data-testid="button-browse-flow-templates"
          >
            <Layout className="w-4 h-4 mr-2" />
            Browse Flow Templates
          </Button>
        </div>
      </div>

      {!flows || flows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <GitBranch className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No flows yet</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              Flows are created from Flow Templates. Pick a template to generate your first
              conversation flow, then customize it here.
            </p>
            <Button
              onClick={() => setLocation('/app/flows/templates')}
              data-testid="button-browse-flow-templates-empty"
            >
              <Layout className="w-4 h-4 mr-2" />
              Browse Flow Templates
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paginatedFlows.map((flow) => (
              <Card key={flow.id} className="hover-elevate" data-testid={`card-flow-${flow.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-5 w-5 text-primary" />
                      <Badge variant={flow.isActive ? "default" : "secondary"} className="text-xs">
                        {flow.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={flow.isActive}
                        onCheckedChange={(checked) =>
                          toggleActiveMutation.mutate({ flowId: flow.id, isActive: checked })
                        }
                        data-testid={`switch-flow-active-${flow.id}`}
                      />
                    </div>
                  </div>
                  <CardTitle className="text-xl" data-testid={`text-flow-name-${flow.id}`}>
                    {flow.name}
                  </CardTitle>
                  {flow.description && (
                    <CardDescription className="text-sm mt-2">{flow.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">
                      {flow.nodes?.length || 0} nodes
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {flow.edges?.length || 0} connections
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Last updated: {formatDate(flow.updatedAt, 'MMM d, yyyy')}
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setLocation(flow.executionConfig?.template?.templateId ? `/app/flows/${flow.id}/customize` : `/app/flows/${flow.id}`)}
                    data-testid={`button-edit-flow-${flow.id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setFlowToTest(flow)}
                    data-testid={`button-test-flow-${flow.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Test
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`button-more-flow-${flow.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => createCampaignFromFlowMutation.mutate({ id: flow.id, name: flow.name })}
                        className="text-violet-600"
                        data-testid={`button-create-campaign-${flow.id}`}
                      >
                        <Rocket className="w-4 h-4 mr-2" />
                        Create Campaign
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setFlowToDelete(flow)}
                        className="text-destructive"
                        data-testid={`button-delete-flow-${flow.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardFooter>
              </Card>
            ))}
          </div>
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[6, 9, 12, 24]}
          />
        </>
      )}

      {/* Test Flow Dialog */}
      {flowToTest && (
        <TestFlowDialog
          open={!!flowToTest}
          onOpenChange={(open) => !open && setFlowToTest(null)}
          flowId={flowToTest.id}
          flowName={flowToTest.name}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!flowToDelete} onOpenChange={(open) => !open && setFlowToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Flow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{flowToDelete?.name}"? This action cannot be undone.
              Any campaigns using this flow will need to be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => flowToDelete && deleteMutation.mutate(flowToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Flow"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
